import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { RefreshCw, ArrowRightLeft, Loader2, TrendingUp } from 'lucide-react';
import { getWalletBalances, getExchangeRates, convertCurrency } from '../lib/api';
import { useTranslation } from '../i18n';
import { Link } from 'react-router-dom';

const QuickExchangeWidget = ({ onExchangeComplete }) => {
  const { t } = useTranslation();
  const [balances, setBalances] = useState([]);
  const [rates, setRates] = useState({});
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [toCurrency, setToCurrency] = useState('EUR');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [balancesRes, ratesRes] = await Promise.all([
          getWalletBalances(),
          getExchangeRates('USD')
        ]);
        setBalances(balancesRes.data || []);
        setRates(ratesRes.data?.rates || {});
      } catch (error) {
        console.error('Error loading exchange data:', error);
      } finally {
        setDataLoading(false);
      }
    };
    fetchData();
  }, []);

  const getBalance = (currency) => {
    const balance = balances.find(b => b.currency === currency);
    return balance?.balance || 0;
  };

  const getConvertedAmount = () => {
    const amt = parseFloat(amount) || 0;
    if (!rates[fromCurrency] || !rates[toCurrency]) return '0.00';
    const rate = rates[toCurrency] / rates[fromCurrency];
    return (amt * rate).toFixed(2);
  };

  const getRate = () => {
    if (!rates[fromCurrency] || !rates[toCurrency]) return '0.0000';
    return (rates[toCurrency] / rates[fromCurrency]).toFixed(4);
  };

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  };

  const handleExchange = async () => {
    const amt = parseFloat(amount);
    
    if (!amt || amt <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (fromCurrency === toCurrency) {
      toast.error('Cannot convert to the same currency');
      return;
    }

    if (amt > getBalance(fromCurrency)) {
      toast.error('Insufficient funds');
      return;
    }

    setLoading(true);
    try {
      const result = await convertCurrency(fromCurrency, toCurrency, amt);
      toast.success(`Exchanged ${amt} ${fromCurrency} to ${result.data.to_amount} ${toCurrency}`);
      setAmount('');
      
      // Refresh balances
      const balancesRes = await getWalletBalances();
      setBalances(balancesRes.data || []);
      
      // Notify parent component
      if (onExchangeComplete) {
        onExchangeComplete();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Exchange failed');
    } finally {
      setLoading(false);
    }
  };

  // Get currencies with balances for "from" dropdown
  const availableCurrencies = balances.length > 0 
    ? balances 
    : [{ currency: 'USD', symbol: '$', balance: 0 }, { currency: 'EUR', symbol: '€', balance: 0 }];

  // Common currencies for "to" dropdown
  const targetCurrencies = [
    { code: 'USD', symbol: '$' },
    { code: 'EUR', symbol: '€' },
    { code: 'GBP', symbol: '£' },
    { code: 'JPY', symbol: '¥' },
    { code: 'CHF', symbol: 'Fr' },
    { code: 'CAD', symbol: 'C$' },
    { code: 'AUD', symbol: 'A$' },
    { code: 'NGN', symbol: '₦' }
  ];

  if (dataLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="quick-exchange-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-purple-500" />
            Quick Exchange
          </CardTitle>
          <Link to="/exchange">
            <Button variant="ghost" size="sm" className="text-xs">
              Full View
              <TrendingUp className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* From Currency */}
        <div className="flex gap-2 items-center">
          <Select value={fromCurrency} onValueChange={setFromCurrency}>
            <SelectTrigger className="w-24" data-testid="quick-from-currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableCurrencies.map((b) => (
                <SelectItem key={b.currency} value={b.currency}>
                  {b.symbol} {b.currency}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="number"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="flex-1"
            data-testid="quick-exchange-amount"
          />
        </div>

        {/* Swap Button & Rate */}
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="icon" 
            className="rounded-full h-8 w-8"
            onClick={swapCurrencies}
            data-testid="quick-swap-btn"
          >
            <ArrowRightLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs text-muted-foreground font-mono">
            1 {fromCurrency} = {getRate()} {toCurrency}
          </span>
        </div>

        {/* To Currency */}
        <div className="flex gap-2 items-center">
          <Select value={toCurrency} onValueChange={setToCurrency}>
            <SelectTrigger className="w-24" data-testid="quick-to-currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {targetCurrencies.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {c.symbol} {c.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex-1 h-10 bg-muted rounded-md flex items-center px-3">
            <span className="font-mono text-sm" data-testid="quick-converted-amount">
              {getConvertedAmount()}
            </span>
          </div>
        </div>

        {/* Balance Info */}
        <p className="text-xs text-muted-foreground">
          Available: {getBalance(fromCurrency).toFixed(2)} {fromCurrency}
        </p>

        {/* Exchange Button */}
        <Button
          onClick={handleExchange}
          disabled={loading || !amount || parseFloat(amount) <= 0}
          className="w-full"
          data-testid="quick-exchange-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Exchanging...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Exchange Now
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default QuickExchangeWidget;
